import type { AuditChange, AuditEntry } from "@divespots/contracts";
import { auditChanges } from "@divespots/contracts";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { api } from "../api/client.ts";
import { Button } from "../components/Button.tsx";
import { formatDateTime } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import {
  AUDIT_ACTIONS,
  AUDIT_ENTITIES,
  auditFieldKey,
  auditFiltersToParams,
  auditNotes,
  auditQuery,
  auditSuggestionPath,
  auditTargetPath,
  auditValueKey,
  formatAuditValue,
  parseAuditFilters,
  truncateText,
} from "./audit.ts";
import type { AuditFilters, AuditNote } from "./audit.ts";

/** Offers line breaks only after "@" and "." (never mid-word); the text stays the address. */
function breakableEmail(email: string) {
  return email.split(/(?<=[@.])/).map((part, i) => (
    <Fragment key={i}>
      {i > 0 ? <wbr /> : null}
      {part}
    </Fragment>
  ));
}

/** `/admin/protokoll` (admins only): who did what, when. Filters live in the URL. */
export function AuditPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [sp, setSp] = useSearchParams();
  const filters = useMemo(() => parseAuditFilters(sp), [sp]);
  const setFilters = (patch: Partial<AuditFilters>) => {
    const next: AuditFilters = { ...filters, ...patch };
    for (const k of Object.keys(next) as (keyof AuditFilters)[])
      if (next[k] === undefined) delete next[k];
    setSp(auditFiltersToParams(next), { replace: true });
  };

  const q = useInfiniteQuery({
    queryKey: ["admin", "audit", filters],
    queryFn: ({ pageParam, signal }) =>
      api.listAudit(auditQuery(filters, pageParam || undefined), signal),
    initialPageParam: "",
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const entries = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.entries), [q.data]);

  // the chips' names: the spot from the shared admin list, the person from the rows seen
  const spots = useQuery({
    queryKey: ["admin", "spots"],
    queryFn: ({ signal }) => api.listSpots({}, signal).then((r) => r.spots),
    enabled: Boolean(filters.spotId),
  });
  const spotName = filters.spotId
    ? (spots.data?.find((s) => s.id === filters.spotId)?.name ?? t("admin.audit.unknownSpot"))
    : null;
  const actorName = filters.actorId
    ? (entries.find((e) => e.actor.id === filters.actorId)?.actor.name ??
      t("admin.audit.unknownPerson"))
    : null;

  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <h1 className="ds-text-title-1 m-0">{t("admin.audit.title")}</h1>
        <p className="ds-text-footnote text-label-secondary m-0">{t("admin.audit.lead")}</p>
      </div>

      <form
        className="adm-filters adm-filters--audit"
        role="search"
        aria-label={t("admin.audit.title")}
        onSubmit={(e) => e.preventDefault()}
      >
        <label>
          <span className="ds-text-footnote text-label-secondary">
            {t("admin.audit.filterEntity")}
          </span>
          <select
            className="adm-input"
            value={filters.entity ?? ""}
            onChange={(e) =>
              setFilters({ entity: (e.target.value || undefined) as AuditFilters["entity"] })
            }
          >
            <option value="">{t("admin.audit.all")}</option>
            {AUDIT_ENTITIES.map((k) => (
              <option key={k} value={k}>
                {t(`admin.audit.entity.${k}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ds-text-footnote text-label-secondary">
            {t("admin.audit.filterAction")}
          </span>
          <select
            className="adm-input"
            value={filters.action ?? ""}
            onChange={(e) =>
              setFilters({ action: (e.target.value || undefined) as AuditFilters["action"] })
            }
          >
            <option value="">{t("admin.audit.all")}</option>
            {AUDIT_ACTIONS.map((k) => (
              <option key={k} value={k}>
                {t(`admin.audit.actions.${k}`)}
              </option>
            ))}
          </select>
        </label>
      </form>

      {spotName || actorName ? (
        <ul className="adm-chips" aria-label={t("admin.audit.activeFilters")}>
          {spotName ? (
            <Chip
              label={t("admin.audit.chipSpot", { name: spotName })}
              remove={t("admin.audit.removeFilter", { name: spotName })}
              onRemove={() => setFilters({ spotId: undefined })}
            />
          ) : null}
          {actorName ? (
            <Chip
              label={t("admin.audit.chipActor", { name: actorName })}
              remove={t("admin.audit.removeFilter", { name: actorName })}
              onRemove={() => setFilters({ actorId: undefined })}
            />
          ) : null}
        </ul>
      ) : null}

      {q.isPending ? <p role="status">{t("common.loading")}</p> : null}
      {q.isError ? (
        <p className="adm-error" role="alert">
          {t("admin.err.load")}
        </p>
      ) : null}
      {q.isSuccess ? (
        <p className="ds-text-footnote text-label-secondary m-0" role="status">
          {entries.length === 0
            ? t("admin.audit.empty")
            : t(q.hasNextPage ? "admin.audit.countMore" : "admin.audit.count", {
                count: entries.length,
              })}
        </p>
      ) : null}

      {entries.length > 0 ? (
        <table className="adm-table adm-audit" aria-label={t("admin.audit.title")}>
          <thead>
            <tr>
              <th scope="col" className="adm-acol-when">
                {t("admin.audit.when")}
              </th>
              <th scope="col" className="adm-acol-who">
                {t("admin.audit.who")}
              </th>
              <th scope="col" className="adm-acol-action">
                {t("admin.audit.action")}
              </th>
              <th scope="col">{t("admin.audit.what")}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <AuditRow
                key={e.id}
                entry={e}
                locale={locale}
                onActor={(id) => setFilters({ actorId: id })}
              />
            ))}
          </tbody>
        </table>
      ) : null}

      {q.hasNextPage ? (
        <div>
          <Button
            variant="tinted"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            {t("admin.audit.loadMore")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Chip({
  label,
  remove,
  onRemove,
}: {
  label: string;
  remove: string;
  onRemove: () => void;
}) {
  return (
    <li className="adm-chip">
      <span>{label}</span>
      <button type="button" className="adm-chip__x" aria-label={remove} onClick={onRemove}>
        <X aria-hidden="true" size={16} strokeWidth={1.75} />
      </button>
    </li>
  );
}

function AuditRow({
  entry: e,
  locale,
  onActor,
}: {
  entry: AuditEntry;
  locale: ReturnType<typeof useLocale>;
  onActor: (id: string) => void;
}) {
  const { t } = useTranslation();
  const changes = useMemo(() => auditChanges(e), [e]);
  const notes = useMemo(() => auditNotes(e), [e]);
  const path = auditTargetPath(e);
  const sugg = auditSuggestionPath(e);
  const label = e.target.label ?? t("admin.audit.unnamed");
  return (
    <tr className="adm-audittr" data-action={e.action}>
      <td className="adm-audittr__when" data-area="when">
        <time dateTime={e.at}>{formatDateTime(e.at, locale)}</time>
      </td>
      <th scope="row" className="adm-audittr__who" data-area="who">
        <Actor actor={e.actor} onActor={onActor} />
      </th>
      <td data-area="action">
        <span className={`ds-badge ${actionBadge(e.action)}`}>
          {t(`admin.audit.actions.${e.action}`)}
        </span>
      </td>
      <td className="adm-audittr__what" data-area="what">
        <span className="ds-text-footnote text-label-secondary">
          {t(`admin.audit.entity.${e.entity}`)}
        </span>{" "}
        {path ? (
          <Link className="adm-audittr__target" to={`/${locale}${path}`}>
            {label}
          </Link>
        ) : (
          <span className="adm-audittr__target">{label}</span>
        )}
        {e.target.deleted && e.entity !== "suggestion" ? (
          <span className="ds-text-footnote text-label-secondary">
            {" "}
            ({t("admin.audit.deletedTarget")})
          </span>
        ) : null}
        {sugg ? (
          <>
            {" · "}
            <Link className="ds-text-footnote" to={`/${locale}${sugg}`}>
              {t("admin.audit.sugg")}
            </Link>
          </>
        ) : null}
        {notes.length > 0 ? (
          <p className="ds-text-footnote text-label-secondary adm-audittr__note">
            {notes.map((n, i) => (
              <Fragment key={i}>
                {i > 0 ? " · " : null}
                <Note note={n} />
              </Fragment>
            ))}
          </p>
        ) : null}
        {changes.length > 0 ? <Changes changes={changes} /> : null}
      </td>
    </tr>
  );
}

function actionBadge(a: AuditEntry["action"]): string {
  return a === "delete" || a === "reject" || a === "withdraw"
    ? "ds-badge--closed"
    : a === "create" || a === "approve" || a === "submit"
      ? "ds-badge--object"
      : "ds-badge--depth";
}

function Actor({ actor, onActor }: { actor: AuditEntry["actor"]; onActor: (id: string) => void }) {
  const { t } = useTranslation();
  if (actor.id !== null) {
    const name = actor.name ?? actor.email ?? actor.id;
    return (
      <>
        <button
          type="button"
          className="adm-linkbtn ds-text-headline"
          title={t("admin.audit.filterBy", { name })}
          aria-label={t("admin.audit.filterBy", { name })}
          onClick={() => onActor(actor.id as string)}
        >
          {name}
        </button>
        {actor.email ? (
          <span className="ds-text-footnote text-label-secondary adm-audittr__email">
            {breakableEmail(actor.email)}
          </span>
        ) : null}
      </>
    );
  }
  return (
    <span className="ds-text-headline adm-audittr__plain">
      {actor.label
        ? t("admin.audit.script", { label: actor.label })
        : t("admin.audit.deletedAccount")}
    </span>
  );
}

function Note({ note }: { note: AuditNote }) {
  const { t } = useTranslation();
  switch (note.kind) {
    case "adjusted":
      return <>{t("admin.audit.adjusted")}</>;
    case "cascaded":
      return <>{t("admin.audit.cascaded")}</>;
    case "redacted":
      return <>{t("admin.audit.redacted")}</>;
    case "text":
      return <>{note.text}</>;
    case "submitKind": {
      const key = `suggest.kind.${note.value}`;
      return <>{t("admin.audit.kind", { kind: t(key, { defaultValue: note.value }) })}</>;
    }
    case "reason":
      return (
        <>
          {t("admin.audit.reason", {
            text: note.text === "account deleted" ? t("admin.audit.accountDeleted") : note.text,
          })}
        </>
      );
  }
}

function Changes({ changes }: { changes: AuditChange[] }) {
  const { t, i18n } = useTranslation();
  const fieldLabel = (f: string) => {
    const key = `admin.audit.field.${auditFieldKey(f)}`;
    return i18n.exists(key) ? t(key) : f;
  };
  return (
    <details className="adm-audittr__details">
      <summary className="ds-text-footnote">
        {t("admin.audit.details", { count: changes.length })}
      </summary>
      <ul className="adm-changes">
        {changes.map((c) => (
          <li key={c.field}>
            <span className="adm-changes__field" title={c.field}>
              {t("admin.audit.fieldLabel", { label: fieldLabel(c.field) })}
            </span>{" "}
            <Value field={c.field} value={c.from} /> → <Value field={c.field} value={c.to} />
          </li>
        ))}
      </ul>
    </details>
  );
}

/** One diff value, cut to a short length with a "more" toggle for the full text. */
function Value({ field, value }: { field: string; value: unknown }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const valueKey = auditValueKey(field, value);
  const full =
    typeof value === "boolean"
      ? t(value ? "admin.audit.yes" : "admin.audit.no")
      : valueKey
        ? t(valueKey)
        : formatAuditValue(value);
  const { text, truncated } = truncateText(full);
  return (
    <span className="adm-changes__value">
      {open ? full : text}
      {truncated ? (
        <>
          {" "}
          <button
            type="button"
            className="adm-linkbtn ds-text-footnote"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            {open ? t("admin.audit.less") : t("admin.audit.more")}
          </button>
        </>
      ) : null}
    </span>
  );
}
